import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, Clock, Plus, Printer, Trash2, X } from "lucide-react";
import { api } from "./api";

/* Horaires partagés : LA référence des cours donnés à plusieurs profs (comédie musicale le
   lundi, comédie musicale et Compagnie junior le jeudi). Refait le 31.10.2026 (Jen : « on voit
   le jour, la date, et pour chaque prof, quels sont ses horaires » ; « un peu trop coloré » ;
   plus d'Excel) :
   · un mois à la fois, une carte par date (lundis, jeudis) ; pour chaque prof une pastille, sa
     plage (« Miller 17h – 17h30 », « Roxane 17h30 – 18h ») et sa place sur la soirée ;
   · en haut, les heures prévues du mois par prof : ce qui arrive dans son décompte ;
   · un clic sur une date : la portée (ce jour, jusqu'aux vacances, toute la saison), les profs et
     leurs heures au quart d'heure, « Pas de cours » (ce jour seulement) ; les trous, deux plages
     d'un prof dans un même cours et un prof déjà dans l'autre salle à la même heure sont signalés.
   Chaque cours de la soirée reçoit la part qui tombe dans son horaire : la paie suit.
   Tous les comptes de la console modifient. Grand chantier : test d'abord. */

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const JOURS_COURT = ["DIM", "LUN", "MAR", "MER", "JEU", "VEN", "SAM"];
const quand = (iso) => new Date(`${iso}T12:00:00`);
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const dateLongue = (iso) => { const d = quand(iso); return `${majuscule(JOURS[d.getDay()])} ${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`; };
const dateCourte = (iso) => { const d = quand(iso); return `${JOURS[d.getDay()].slice(0, 3)}. ${d.getDate()} ${MOIS_COURT[d.getMonth()]}`; };
const nomMois = (m) => { const [a, n] = m.split("-").map(Number); return `${majuscule(MOIS[n - 1])} ${a}`; };
const h = (t) => String(t || "").replace(/^0/, "").replace(":00", "h").replace(":", "h");
const enMin = (t) => { const [a, b] = String(t || "0:0").split(":").map(Number); return a * 60 + (b || 0); };
const enHeure = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const duree = (m) => (m <= 0 ? "—" : `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, "0") : ""}`);
const prenom = (nom) => String(nom || "").split(" ")[0];
const cle = (nom) => prenom(nom).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
// La date du jour ici (et non en UTC : entre minuit et 1h–2h, « prochain » restait sur la veille).
const aujourdhui = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// Une pastille par prof (les mêmes couleurs que les cours privés), le reste en gris.
const COULEURS = { noemie: "#7C3AED", lydia: "#DB2777", miller: "#0891B2", roxane: "#D97706" };
const AUTRES = ["#16A34A", "#4F46E5", "#0F766E", "#B45309", "#475569"];
const couleur = (nom) => COULEURS[cle(nom)] || AUTRES[[...cle(nom)].reduce((n, c) => n + c.charCodeAt(0), 0) % AUTRES.length];
const Pastille = ({ nom, taille = 8 }) => (
  <span className="inline-block rounded-full shrink-0" style={{ width: taille, height: taille, background: couleur(nom) }} />
);
const ORANGE = "#B45309";
const TRAIT = "var(--border)";

/* La place d'une plage sur la soirée : une fine piste grise, la part du prof dans sa couleur. */
const Piste = ({ soiree, debut, fin, nom, haut = 6, doux = false, className = "" }) => {
  const d0 = enMin(soiree.debut);
  const total = Math.max(1, enMin(soiree.fin) - d0);
  const g = Math.max(0, (enMin(debut) - d0) / total) * 100;
  const l = Math.max(0, Math.min(100 - g, ((enMin(fin) - enMin(debut)) / total) * 100));
  return (
    <div className={`relative rounded-full ${className}`} style={{ height: haut, background: "#EEF2F6" }}>
      <div className="absolute top-0 bottom-0 rounded-full" style={{ left: `${g}%`, width: `${l}%`, background: couleur(nom), opacity: doux ? 0.45 : 1 }} />
    </div>
  );
};

/* Les trous de la soirée (personne) et les profs déjà dans l'autre salle à la même heure. */
const trous = (soiree, lignes) => {
  const plages = lignes.map((p) => [enMin(p.debut), enMin(p.fin)]).filter(([a, b]) => b > a).sort((a, b) => a[0] - b[0]);
  const out = [];
  let t = enMin(soiree.debut);
  plages.forEach(([a, b]) => { if (a > t) out.push([t, a]); t = Math.max(t, b); });
  if (t < enMin(soiree.fin)) out.push([t, enMin(soiree.fin)]);
  return out;
};
const conflits = (lignes, autres) => {
  const out = [];
  autres.forEach(({ soiree, seance }) => (seance.profs || []).forEach((q) => lignes.forEach((p) => {
    if (cle(p.nom) === cle(q.nom) && enMin(p.debut) < enMin(q.fin) && enMin(q.debut) < enMin(p.fin)) {
      out.push(`${prenom(p.nom)} est aussi en ${soiree.titre} de ${h(q.debut)} à ${h(q.fin)}.`);
    }
  })));
  return [...new Set(out)];
};
/* Un prof avec deux plages séparées dans le même cours (une seule part par cours pour la
   paie) : dit avant « Enregistrer » (le serveur le refuse aussi). Deux plages qui se touchent
   ou se chevauchent se rejoignent à l'enregistrement. */
const deuxPlages = (soiree, lignes) => {
  const out = [];
  soiree.cours.forEach((c) => {
    const parProf = {};
    lignes.forEach((l) => {
      const a = Math.max(enMin(l.debut), enMin(c.debut));
      const b = Math.min(enMin(l.fin), enMin(c.fin));
      if (b > a) (parProf[cle(l.nom)] ||= { nom: l.nom, plages: [] }).plages.push([a, b]);
    });
    Object.values(parProf).forEach(({ nom, plages }) => {
      plages.sort((x, y) => x[0] - y[0]);
      let f = plages[0][1];
      if (plages.slice(1).some(([a, b]) => { const separee = a > f; f = Math.max(f, b); return separee; })) {
        out.push(`${prenom(nom)} a deux plages dans « ${c.nom} » : une seule plage par cours.`);
      }
    });
  });
  return out;
};

/* Les durées annoncées par les profs dans leur application (« ma part du cours ») :
   l'école applique la répartition proposée (le collègue qui prenait le relais suit, sa paie
   aussi) ou la refuse (la durée prévue revient). */
const parts = (liste) => (liste || []).map((p) => `${prenom(p.nom)} ${h(p.debut)}–${h(p.fin)}`).join(" · ");
const Propositions = ({ liste, peutModifier, agir, onMessage, onRegler }) => {
  if (!liste?.length) return null;
  // Le message part en haut de l'écran : ce bloc disparaît avec la dernière proposition, et
  // son message avec lui (test du 01.11.2026 : rien ne disait que c'était fait).
  const decider = (p, action) => agir(`/horaires-partages/propositions/${p.id}/${action}`, "POST", null, (r) => onMessage(r?.message || ""));
  return (
    <div className="rounded-2xl border bg-white" style={{ borderColor: TRAIT }} data-testid="horaires-propositions">
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 font-semibold text-sm"><Clock size={15} style={{ color: ORANGE }} /> Propositions des profs · {liste.length}</div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Un prof a annoncé une autre durée pour sa part (elle compte déjà dans son décompte) ou, dans une alternance, dit
          avoir donné le cours à la place de son collègue (rien ne compte avant ta décision). « Appliquer » règle la date
          (la paie suit), « Refuser » garde ce qui était prévu.
        </p>
      </div>
      {liste.map((p) => (
        <div key={p.id} className="border-t px-4 py-3 text-sm flex flex-wrap items-center gap-3" style={{ borderColor: TRAIT }} data-testid="horaires-proposition">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2"><Pastille nom={p.prof} /><strong>{p.prof}</strong><span className="text-[var(--muted)]">· {p.cours} · {dateCourte(p.date)}</span></div>
            {/* Alternance échangée au dernier moment (Jen, 02.11.2026). */}
            {p.type === "reprise"
              ? <div className="text-xs mt-1" data-testid="horaires-proposition-reprise">A donné le cours <strong>à la place de {p.a_la_place_de || "son collègue"}</strong> ({h(p.debut)}–{h(p.fin)})</div>
              : <div className="text-xs mt-1">Annonce <strong>{p.minutes} min</strong> au lieu de {p.prevu} min</div>}
            <div className="text-xs text-[var(--muted)] mt-0.5">Avant : {parts(p.avant) || "—"}{p.propose ? <> · Proposé : <span className="text-[var(--text)]">{parts(p.propose)}</span></> : null}</div>
            {!p.propose && <div className="text-xs mt-0.5" style={{ color: ORANGE }}>Pas encore de répartition ce jour-là : règle d'abord la date, puis décide.</div>}
          </div>
          {peutModifier && (
            <div className="flex gap-2">
              {p.propose && <button className="btn-primary text-xs" onClick={() => decider(p, "appliquer")} data-testid="horaires-proposition-appliquer"><Check size={13} /> Appliquer</button>}
              {!p.propose && onRegler && <button className="btn-primary text-xs" onClick={() => onRegler(p)} data-testid="horaires-proposition-regler"><Clock size={13} /> Régler la date</button>}
              <button className="btn-ghost text-xs" onClick={() => decider(p, "refuser")} data-testid="horaires-proposition-refuser"><X size={13} /> Refuser</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* Une soirée d'une date dans la liste : son nom et ses heures, puis une ligne par prof. */
const BlocSoiree = ({ soiree, seance, manquants }) => {
  const enTete = (
    <div className="flex items-baseline justify-between gap-3 text-xs text-[var(--muted)]">
      <span className="min-w-0">{soiree.titre}</span>
      <span className="tabular-nums shrink-0">{h(soiree.debut)} – {h(soiree.fin)}</span>
    </div>
  );
  if (seance.ferme) {
    return <div>{enTete}<div className="text-sm mt-1 text-[var(--muted)]">Pas de cours · {seance.note || "fermé"}</div></div>;
  }
  return (
    <div>
      {enTete}
      {seance.profs.length ? (
        <div className="mt-1.5 space-y-1.5">
          {seance.profs.map((p, i) => (
            <div key={`${p.nom}-${i}`} className="flex items-center gap-2 sm:gap-3 text-sm" data-testid="horaires-prof">
              <Pastille nom={p.nom} />
              {/* Sur téléphone, « Noémie » devenait « Noé… » : le prénom ne rétrécit plus. */}
              <span className="w-[4.5rem] shrink-0 font-medium truncate">{prenom(p.nom)}</span>
              <span className="tabular-nums sm:w-[6.5rem] shrink-0">{h(p.debut)} – {h(p.fin)}</span>
              <Piste soiree={soiree} debut={p.debut} fin={p.fin} nom={p.nom} haut={4} doux className="hidden sm:block flex-1 max-w-[240px]" />
              <span className="text-xs text-[var(--muted)] tabular-nums ml-auto">{duree(enMin(p.fin) - enMin(p.debut))}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm mt-1" style={{ color: ORANGE }}>
          Pas encore réglé <span className="text-[var(--muted)]">· au planning : {soiree.profs.map(prenom).join(", ")}</span>
        </div>
      )}
      {seance.profs.length > 0 && manquants.length > 0 && (
        <div className="text-xs mt-1.5" style={{ color: ORANGE }}>À compléter : {manquants.join(", ")}</div>
      )}
      {/* Un trou dans la soirée (une durée annoncée appliquée, une plage raccourcie) : vu
          aussi dans la liste, pas seulement dans la fenêtre (test du 01.11.2026). */}
      {seance.profs.length > 0 && !manquants.length && trous(soiree, seance.profs).map(([a, b]) => (
        <div key={a} className="text-xs mt-1.5" style={{ color: ORANGE }} data-testid="horaires-trou">Personne de {h(enHeure(a))} à {h(enHeure(b))}</div>
      ))}
    </div>
  );
};

/* Régler une date : les profs et leurs heures au quart d'heure. */
/* Les noms des cours d'une soirée sans leur début commun (« Comédie musicale 7-9 ans ·
   10-14 ans · adultes » → « 7-9 ans », « 10-14 ans », « adultes ») : le titre de la fenêtre
   dit déjà « Comédie musicale », et sur téléphone les noms entiers étaient coupés. */
const nomsCourts = (cours) => {
  const mots = cours.map((c) => String(c.nom || "").split(" "));
  let k = 0;
  while (mots.every((m) => m.length > k + 1 && m[k] === mots[0][k])) k += 1;
  return Object.fromEntries(cours.map((c, i) => [c.id, k ? mots[i].slice(k).join(" ") : c.nom]));
};

/* « Ce que chacun reçoit » (Jen, 02.11.2026 : « comment tu fais la réconciliation des minutes
   par cours ? » ; « super userfriendly, Apple style ») : la soirée se règle d'un bloc, la paie
   est par cours (chaque cours a ses élèves, donc son tarif). Une liste groupée façon iPhone :
   chaque cours, son horaire, et la part de chaque prof (pastille de sa couleur) ; en bas le
   total de la soirée. C'est ce que le serveur fait de la saisie, en direct avant
   « Enregistrer ». Sur téléphone, les parts passent sous le nom du cours. */
const enClair = (m) => (m <= 0 ? "" : m < 60 ? `${m} min` : duree(m));
const PartProf = ({ nom, minutes, fort = false }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] tabular-nums whitespace-nowrap ${fort ? "font-semibold" : ""}`}
    style={{ background: "#F1F5F9" }}>
    <Pastille nom={nom} />{prenom(nom)}<span className="text-[var(--muted)] font-normal">·</span>{enClair(minutes)}
  </span>
);
const ParCours = ({ soiree, lignes }) => {
  const qui = [...new Set(lignes.map((l) => cle(l.nom)))].map((k) => lignes.find((l) => cle(l.nom) === k).nom);
  const part = (c, nom) => lignes.filter((l) => cle(l.nom) === cle(nom)).reduce((s, l) =>
    s + Math.max(0, Math.min(enMin(c.fin), enMin(l.fin)) - Math.max(enMin(c.debut), enMin(l.debut))), 0);
  const courts = nomsCourts(soiree.cours);
  return (
    <div data-testid="horaires-par-cours">
      <div className="px-1 pb-1.5 text-[13px] font-semibold">Ce que chacun reçoit</div>
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: TRAIT }}>
        {soiree.cours.map((c, i) => {
          const parts = qui.map((n) => [n, part(c, n)]).filter(([, m]) => m > 0);
          return (
            <div key={c.id} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1.5" style={i ? { borderTop: `1px solid ${TRAIT}` } : undefined}
              data-testid="horaires-par-cours-ligne">
              <div className="min-w-0 sm:flex-1">
                <div className="text-[14.5px] leading-snug">{courts[c.id]}</div>
                <div className="text-xs text-[var(--muted)] tabular-nums">{h(c.debut)} – {h(c.fin)}</div>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:justify-end">
                {parts.length ? parts.map(([n, m]) => <PartProf key={n} nom={n} minutes={m} />)
                  : <span className="text-[13px]" style={{ color: ORANGE }}>Personne</span>}
              </div>
            </div>
          );
        })}
        <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1.5" style={{ borderTop: `1px solid ${TRAIT}`, background: "#FAFBFC" }}>
          <div className="text-[14.5px] font-semibold sm:flex-1">Total de la soirée</div>
          <div className="flex flex-wrap gap-1.5 sm:justify-end">
            {qui.map((n) => (
              <span key={n} data-testid={`horaires-par-cours-total-${prenom(n)}`} data-minutes={soiree.cours.reduce((t, c) => t + part(c, n), 0)}>
                <PartProf nom={n} minutes={soiree.cours.reduce((t, c) => t + part(c, n), 0)} fort />
              </span>
            ))}
          </div>
        </div>
      </div>
      <p className="px-1 pt-1.5 text-xs text-[var(--muted)]">Payé cours par cours, selon le nombre d'élèves de chaque cours.</p>
    </div>
  );
};

/* Les profs d'une soirée et leurs heures au quart d'heure : la fenêtre d'une date, et chaque
   équipe de « Préparer la saison » (Jen, 02.11.2026). `recu` : « Ce que chacun reçoit » en
   dessous (pas pour chaque semaine d'une alternance : trop long). */
const EditeurParts = ({ soiree, lignes, setLignes, autres = [], recu = true }) => {
  const heures = useMemo(() => {
    const out = [];
    for (let m = enMin(soiree.debut); m <= enMin(soiree.fin); m += 15) out.push(enHeure(m));
    return out;
  }, [soiree]);
  const maj = (i, champs) => setLignes(lignes.map((l, k) => (k === i ? { ...l, ...champs } : l)));
  const ajouter = (nom) => {
    // Le nouveau prof prend le premier moment sans personne (« Personne de 17h à 17h30 » →
    // 17h – 17h30), sinon le relais là où le dernier s'arrête (sinon toute la soirée).
    const trou = trous(soiree, lignes)[0];
    if (trou) { setLignes([...lignes, { nom, debut: enHeure(trou[0]), fin: enHeure(trou[1]) }]); return; }
    const fins = lignes.map((l) => enMin(l.fin)).filter((m) => m < enMin(soiree.fin));
    const debut = fins.length ? Math.max(...fins) : enMin(soiree.debut);
    setLignes([...lignes, { nom, debut: enHeure(debut), fin: soiree.fin }]);
  };
  const vides = trous(soiree, lignes);
  const doubles = [...conflits(lignes, autres), ...deuxPlages(soiree, lignes)];
  return (
    <>
      <div className="rounded-2xl border bg-white" style={{ borderColor: TRAIT }}>
        {lignes.length === 0 && <div className="px-4 py-3 text-sm text-[var(--muted)]">Personne pour l'instant : ajoute un prof ci-dessous.</div>}
        {lignes.map((l, i) => (
          <div key={i} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" style={i ? { borderTop: `1px solid ${TRAIT}` } : undefined} data-testid={`horaires-part-${prenom(l.nom)}`}>
            <span className="flex items-center gap-2 sm:w-24 font-medium flex-1 sm:flex-none"><Pastille nom={l.nom} taille={10} />{prenom(l.nom)}</span>
            <span className="flex items-center gap-1.5 order-last sm:order-none w-full sm:w-auto">
              <select className="input-field !w-auto !py-1.5 tabular-nums" value={l.debut} onChange={(e) => maj(i, { debut: e.target.value })} aria-label="De" data-testid="horaires-de">
                {heures.slice(0, -1).map((t) => <option key={t} value={t}>{h(t)}</option>)}
              </select>
              <span className="text-[var(--muted)]">à</span>
              <select className="input-field !w-auto !py-1.5 tabular-nums" value={l.fin} onChange={(e) => maj(i, { fin: e.target.value })} aria-label="À" data-testid="horaires-a">
                {heures.slice(1).map((t) => <option key={t} value={t}>{h(t)}</option>)}
              </select>
            </span>
            <span className="text-xs text-[var(--muted)] tabular-nums sm:w-12 text-right">{duree(enMin(l.fin) - enMin(l.debut))}</span>
            <button type="button" className="sm:ml-auto text-[var(--muted)] hover:text-[#B91C1C] p-1" onClick={() => setLignes(lignes.filter((_, k) => k !== i))}
              aria-label={`Retirer ${prenom(l.nom)}`} data-testid="horaires-retirer"><X size={15} /></button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--muted)] mr-1">Ajouter</span>
        {soiree.profs.map((n) => (
          <button key={n} type="button" onClick={() => ajouter(n)} data-testid={`horaires-ajouter-${prenom(n)}`}
            className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-sm hover:bg-[#F8FAFC]" style={{ borderColor: TRAIT }}>
            <Plus size={12} /><Pastille nom={n} />{prenom(n)}
          </button>
        ))}
      </div>

      {recu && lignes.length > 0 && <ParCours soiree={soiree} lignes={lignes} />}

      {(vides.length > 0 || doubles.length > 0) && lignes.length > 0 && (
        <div className="space-y-1 text-xs" style={{ color: ORANGE }} data-testid="horaires-alertes">
          {vides.map(([a, b]) => <div key={a} className="flex items-center gap-1.5"><AlertTriangle size={12} /> Personne de {h(enHeure(a))} à {h(enHeure(b))}.</div>)}
          {doubles.map((t) => <div key={t} className="flex items-center gap-1.5"><AlertTriangle size={12} /> {t}</div>)}
        </div>
      )}
    </>
  );
};
const partsValides = (lignes) => lignes.length > 0 && lignes.every((l) => enMin(l.fin) > enMin(l.debut));

const Editeur = ({ soiree, seance, suivantes, saison, autres, envoi, erreur, onFermer, onEnregistrer, onEffacer }) => {
  const [lignes, setLignes] = useState(() => (seance.profs || []).map((p) => ({ ...p })));
  const [ferme, setFerme] = useState(!!seance.ferme);
  const [note, setNote] = useState(seance.ferme ? seance.note || "" : "");
  const [portee, setPortee] = useState("jour");     // jour | vacances | saison
  useEffect(() => {
    const touche = (e) => { if (e.key === "Escape") onFermer(); };
    window.addEventListener("keydown", touche);
    return () => window.removeEventListener("keydown", touche);
  }, [onFermer]);
  const invalide = !ferme && !partsValides(lignes);
  const corps = ferme ? { ferme: true, note, profs: [] } : { profs: lignes.map(({ nom, debut, fin }) => ({ nom, debut, fin })) };
  const jourSemaine = JOURS[quand(seance.date).getDay()];
  const total = Math.max(1, enMin(soiree.fin) - enMin(soiree.debut));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6" style={{ background: "rgba(15,27,45,0.45)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="bg-white w-full sm:max-w-[600px] rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-auto shadow-2xl" data-testid="horaires-repartition">
        <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: TRAIT }}>
          <div className="min-w-0">
            <div className="font-display text-xl tracking-tight">{dateLongue(seance.date)}</div>
            <div className="text-xs text-[var(--muted)] mt-0.5">{soiree.titre} · {h(soiree.debut)} – {h(soiree.fin)}</div>
          </div>
          <button className="ml-auto text-[var(--muted)] hover:text-[var(--text)] p-1" onClick={onFermer} aria-label="Fermer" data-testid="horaires-fermer"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* D'abord : ce jour seulement, jusqu'aux vacances, ou toute la saison (Jen, 31.10.2026 :
              « on pourrait appliquer à tous ? » ; les vacances et les fériés sont sautés). */}
          {/* « Pas de cours ce jour-là » ne vaut que pour ce jour (test du 01.11.2026 : avec
              « Toute la saison » choisi avant, toute la saison se fermait). */}
          {saison > 0 && !ferme && (
            <div>
              <div className="flex w-full rounded-full p-0.5 text-sm" style={{ background: "#F1F5F9" }} data-testid="horaires-portee">
                {[["jour", "Ce jour seulement"], ...(suivantes > 0 && suivantes < saison ? [["vacances", `Jusqu'aux vacances (${suivantes + 1})`]] : []),
                  ["saison", `Toute la saison (${saison + 1})`]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPortee(v)} data-testid={`horaires-portee-${v}`}
                    className="flex-1 px-2 py-1.5 rounded-full leading-tight sm:whitespace-nowrap" style={portee === v ? { background: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(15,27,45,.12)" } : { color: "var(--muted)" }}>{l}</button>
                ))}
              </div>
              <p className="text-xs text-[var(--muted)] mt-2">
                {portee === "jour" ? `Seulement le ${dateLongue(seance.date).toLowerCase()}.`
                  : `Ce ${jourSemaine} et les ${portee === "saison" ? saison : suivantes} suivants${portee === "saison" ? ", jusqu'à la fin de la saison" : ", jusqu'aux prochaines vacances"}. Les vacances et les fériés sont sautés ; un jour déjà fermé par l'école reste fermé.`}
              </p>
            </div>
          )}
          {!ferme && (
            <>
              {/* La soirée en un coup d'œil : les cours (repères) et la part de chaque prof. */}
              <div>
                <div className="relative h-4 text-[10px] text-[var(--muted)] tabular-nums">
                  {[...soiree.cours.map((c) => c.debut), soiree.fin].map((t, i, tout) => (
                    <span key={`${t}-${i}`} className="absolute" style={{
                      left: `${((enMin(t) - enMin(soiree.debut)) / total) * 100}%`,
                      transform: i === 0 ? "none" : i === tout.length - 1 ? "translateX(-100%)" : "translateX(-50%)",
                    }}>{h(t)}</span>
                  ))}
                </div>
                <div className="space-y-1 mt-1">
                  {lignes.length ? lignes.map((l, i) => <Piste key={i} soiree={soiree} debut={l.debut} fin={l.fin} nom={l.nom} haut={8} />)
                    : <div className="h-2 rounded-full" style={{ background: "#EEF2F6" }} />}
                </div>
                <div className="flex text-[10px] text-[var(--muted)] mt-1">
                  {soiree.cours.map((c) => (
                    <span key={c.id} className="truncate pr-1" title={c.nom} style={{ width: `${((enMin(c.fin) - enMin(c.debut)) / total) * 100}%` }}>{nomsCourts(soiree.cours)[c.id]}</span>
                  ))}
                </div>
              </div>

              <EditeurParts soiree={soiree} lignes={lignes} setLignes={setLignes} autres={autres} />
            </>
          )}

          <div className="space-y-2.5 text-sm">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={ferme} onChange={(e) => setFerme(e.target.checked)} data-testid="horaires-pas-de-cours" />
              Pas de cours ce jour-là
            </label>
            {ferme && <input className="input-field text-sm" placeholder="Pourquoi : Spectacle de Noël, répétition…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="horaires-pourquoi" />}
          </div>
          {erreur && <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }} data-testid="horaires-erreur">{erreur}</p>}
        </div>

        <div className="px-6 py-4 border-t flex flex-wrap items-center gap-2" style={{ borderColor: TRAIT }}>
          {onEffacer && (
            <button className="btn-ghost text-sm" style={{ color: "#B91C1C" }} onClick={onEffacer} data-testid="horaires-revenir-habituel" title="Plus personne d'assigné ce jour-là">
              <Trash2 size={14} /> Effacer ce jour
            </button>
          )}
          <button className="btn-ghost text-sm ml-auto" onClick={onFermer}>Annuler</button>
          <button className="btn-primary text-sm" disabled={invalide || envoi} data-testid="horaires-enregistrer" onClick={() => onEnregistrer(corps, ferme ? "jour" : portee)}>
            <Check size={14} /> {envoi ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* La feuille à imprimer (Jen, 31.10.2026 : « l'imprimer un peu comme le planning et la
   partager aux profs ») : une ligne par semaine, une colonne par soirée (comme le tableau
   Excel de l'école), pour chaque prof sa plage. « Pour » un seul prof : ses dates et ses heures
   seulement, avec le total — à imprimer ou à enregistrer en PDF pour le lui envoyer. */
const lundiDe = (iso) => { const d = quand(iso); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
// Les couleurs douces du tableau de l'école : une par équipe de profs (la plus fréquente d'abord).
const TEINTES_EQUIPES = ["#FEF3C7", "#DBEAFE", "#FCE7F3", "#DCFCE7", "#EDE9FE", "#FFEDD5", "#CCFBF1", "#E0E7FF"];
const equipe = (liste) => (liste || []).map((p) => prenom(p.nom)).sort().join(" + ");
const quiTexte = (liste) => {
  const n = [...new Set((liste || []).map((p) => prenom(p.nom)))];
  return n.length > 1 ? `${n.slice(0, -1).join(", ")} et ${n[n.length - 1]}` : n[0] || "";
};
// « 17h – 20h45 », ou « 17h – 20h45 (Lydia : 17h30 – 20h45) » quand une part diffère — comme l'Excel.
const horaireTexte = (liste) => {
  if (!liste?.length) return "";
  const d = liste.reduce((m, p) => (p.debut < m ? p.debut : m), liste[0].debut);
  const f = liste.reduce((m, p) => (p.fin > m ? p.fin : m), liste[0].fin);
  const autres = liste.filter((p) => p.debut !== d || p.fin !== f).map((p) => `${prenom(p.nom)} : ${h(p.debut)} – ${h(p.fin)}`);
  // Personne sur toute la soirée (des relais) : seulement les plages, sans la parenthèse.
  if (liste.length > 1 && autres.length === liste.length) return liste.map((p) => `${prenom(p.nom)} ${h(p.debut)} – ${h(p.fin)}`).join(" · ");
  return `${h(d)} – ${h(f)}${autres.length ? ` (${autres.join(", ")})` : ""}`;
};

/* La feuille à imprimer (Jen, 31.10.2026 : « jolie, style calendrier, comme le fichier Excel ») :
   comme le tableau « Horaires 2026 - 2027 » de l'école — pour chaque soirée « Date · Profs ·
   Horaire », une couleur douce par équipe de profs, les vacances en gris, « Pas de cours » en
   rose, un bandeau par mois. « Pour » un seul prof : ses dates et ses heures, avec son total —
   à imprimer ou à enregistrer en PDF pour le lui envoyer. */
const Impression = ({ soirees, parDate, mois, saison = "", aujourdhui: ajd, onRetour }) => {
  const [pour, setPour] = useState("");
  const [periode, setPeriode] = useState("a_venir");
  const profsTous = [...new Set(soirees.flatMap((so) => so.profs.map(prenom)))];
  const dates = Object.keys(parDate).sort().filter((x) => (periode === "mois" ? x.startsWith(mois) : periode === "a_venir" ? x >= ajd : true));
  const semaines = {};
  dates.forEach((x) => { (semaines[lundiDe(x)] ||= {}); parDate[x].forEach((e) => { semaines[lundiDe(x)][e.soiree.id] = e.seance; }); });
  // Les équipes et leur couleur, la plus fréquente d'abord (comme les couleurs de l'Excel).
  const freq = {};
  dates.forEach((x) => parDate[x].forEach(({ seance }) => { if (!seance.ferme && seance.profs.length) freq[equipe(seance.profs)] = (freq[equipe(seance.profs)] || 0) + 1; }));
  const couleurEquipe = Object.fromEntries(Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([e], i) => [e, TEINTES_EQUIPES[i % TEINTES_EQUIPES.length]]));
  let total = 0;
  const bord = { borderBottom: "1px solid #fff", borderRight: "1px solid #fff" };
  // Les cellules d'une soirée dans la ligne d'une semaine. Leurs clés portent la soirée : avec
  // les mêmes clés pour le lundi et le jeudi, changer « Pour … » ou la période laissait des
  // cellules d'avant sur la feuille (test du 01.11.2026).
  const lesSiennes = (seance) => (seance && !seance.ferme ? seance.profs.filter((p) => !pour || prenom(p.nom) === pour) : []);
  const cellules = (so, seance) => {
    const k = (x) => `${so.id}-${x}`;
    const jour = "px-2 py-1.5 font-semibold whitespace-nowrap";
    if (!seance) return [<td key={k("v")} colSpan={3} style={bord} />];
    if (seance.source === "calendrier") {
      return [<td key={k("d")} className={jour} style={{ ...bord, background: "#94A3B8", color: "#fff" }}>{dateCourte(seance.date)}</td>,
        <td key={k("n")} colSpan={2} className="px-2 py-1.5 italic" style={{ ...bord, background: "#94A3B8", color: "#fff" }}>{seance.note || "Vacances"}</td>];
    }
    if (seance.ferme) {
      return [<td key={k("d")} className={jour} style={{ ...bord, background: "#FCE7F3" }}>{dateCourte(seance.date)}</td>,
        <td key={k("n")} colSpan={2} className="px-2 py-1.5 italic" style={{ ...bord, background: "#FCE7F3", color: "#9D174D" }}>Pas de cours · {seance.note}</td>];
    }
    const lignes = lesSiennes(seance);
    lignes.forEach((p) => { total += enMin(p.fin) - enMin(p.debut); });
    if (!lignes.length) {
      // Pour un prof : rien les jours où il n'enseigne pas (ses dates seulement).
      if (pour) return [<td key={k("v")} colSpan={3} style={bord} />];
      return [<td key={k("d")} className={jour} style={bord}>{dateCourte(seance.date)}</td>,
        <td key={k("n")} colSpan={2} className="px-2 py-1.5 text-[var(--muted)]" style={bord}>à régler</td>];
    }
    const fond = { ...bord, background: pour ? "#E0F2FE" : couleurEquipe[equipe(seance.profs)] || "#F8FAFC" };
    return [
      <td key={k("d")} className={jour} style={fond}>{dateCourte(seance.date)}</td>,
      <td key={k("q")} className="px-2 py-1.5" style={fond}>{quiTexte(lignes)}</td>,
      <td key={k("h")} className="px-2 py-1.5 tabular-nums" style={fond}>{horaireTexte(lignes)}</td>,
    ];
  };
  const titrePeriode = periode === "mois" ? nomMois(mois) : periode === "a_venir" ? `dès le ${dateCourte(ajd)}` : `toute la saison${saison ? ` ${saison}` : ""}`;
  const lignesTableau = [];
  let moisCourant = "";
  // Pour un prof : seulement les semaines où il enseigne (et les vacances, les jours fermés).
  const garder = (l) => !pour || Object.values(semaines[l]).some((s) => s.source === "calendrier" || s.ferme || lesSiennes(s).length);
  Object.keys(semaines).sort().filter(garder).forEach((l) => {
    const premiere = Object.values(semaines[l])[0]?.date || l;
    if (premiere.slice(0, 7) !== moisCourant) {
      moisCourant = premiere.slice(0, 7);
      lignesTableau.push(
        <tr key={`m-${moisCourant}`} className="break-inside-avoid">
          <td colSpan={soirees.length * 3} className="pt-3 pb-1 px-1 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#0F4C5C" }}>{nomMois(moisCourant)}</td>
        </tr>,
      );
    }
    lignesTableau.push(<tr key={l} className="align-top break-inside-avoid">{soirees.flatMap((so) => cellules(so, semaines[l][so.id]))}</tr>);
  });
  return (
    <div className="space-y-4" data-testid="horaires-impression">
      <div className="sans-impression flex flex-wrap items-center gap-2">
        <button type="button" className="btn-ghost text-sm" onClick={onRetour}><ArrowLeft size={14} /> Retour</button>
        <select className="input-field !w-auto !py-1.5 text-sm" value={pour} onChange={(e) => setPour(e.target.value)} data-testid="horaires-impression-pour">
          <option value="">Pour tous les profs</option>
          {profsTous.map((n) => <option key={n} value={n}>Pour {n}</option>)}
        </select>
        <select className="input-field !w-auto !py-1.5 text-sm" value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="horaires-impression-periode">
          <option value="a_venir">Dès aujourd'hui</option>
          <option value="mois">{nomMois(mois)}</option>
          <option value="saison">Toute la saison{saison ? ` ${saison}` : ""}</option>
        </select>
        <button type="button" className="btn-primary text-sm ml-auto" onClick={() => window.print()} data-testid="horaires-impression-imprimer">
          <Printer size={14} /> Imprimer ou enregistrer en PDF
        </button>
      </div>
      <p className="sans-impression text-[11px] text-[var(--muted)]">
        Pour l'envoyer à un prof : choisis « Pour … », puis « Enregistrer en PDF » dans la fenêtre d'impression. Décoche « En-têtes et pieds de page »
        dans « Plus de paramètres » : sinon la date et l'adresse du site s'impriment en haut et en bas.
      </p>
      <div className="fiche-impression">
        <div className="feuille bg-white rounded-2xl border p-6" style={{ borderColor: TRAIT }}>
          <div className="feuille-entete flex flex-wrap items-end justify-between gap-3 pb-3 mb-3" style={{ borderBottom: "3px solid var(--primary)" }}>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--muted)]">1000 ARTS · Cugy VD · Horaires partagés{saison ? ` ${saison}` : ""}</div>
              <div className="font-display text-3xl tracking-tight leading-none mt-1">Comédie musicale{pour ? ` · ${pour}` : ""}</div>
            </div>
            <div className="text-sm text-[var(--muted)]">{majuscule(titrePeriode)}</div>
          </div>
          <table className="w-full text-[11.5px] border-collapse table-fixed">
            <colgroup>
              {/* La date (« lun. 16 nov. ») débordait sur les profs sur la feuille A4 (9 %). */}
              {soirees.flatMap((so) => [12, 13, 25].map((w, k) => <col key={`${so.id}-${k}`} style={{ width: `${(w * 2) / soirees.length}%` }} />))}
            </colgroup>
            <thead>
              <tr style={{ background: "#0F4C5C", color: "#fff" }}>
                {soirees.map((so, i) => (
                  <th key={so.id} colSpan={3} className="px-2 py-2 text-left font-semibold" style={i ? { borderLeft: "3px solid #fff" } : undefined}>
                    {majuscule(so.jour)} · {h(so.debut)} – {h(so.fin)}
                    <div className="text-[10px] font-normal opacity-80">{so.titre}</div>
                  </th>
                ))}
              </tr>
              <tr className="text-[10px] uppercase tracking-wide" style={{ background: "#155E75", color: "#fff" }}>
                {soirees.flatMap((so, i) => ["Date", "Profs", "Horaire"].map((t, k) => (
                  <th key={`${so.id}-${t}`} className="px-2 py-1 text-left font-medium" style={i && !k ? { borderLeft: "3px solid #fff" } : undefined}>{t}</th>
                )))}
              </tr>
            </thead>
            <tbody>{lignesTableau}</tbody>
          </table>
          <div className="pied-feuille mt-3 text-[10.5px] text-[var(--muted)] flex flex-wrap items-center justify-between gap-3">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {pour ? `Total ${pour} : ${duree(total)} sur la période.` : <>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-2.5 rounded-sm" style={{ background: "#94A3B8" }} /> vacances, férié</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-2.5 rounded-sm" style={{ background: "#FCE7F3" }} /> pas de cours</span>
                <span>une couleur par équipe de profs · les heures du mois de chaque prof en découlent</span>
              </>}
            </span>
            <span>Imprimé le {dateCourte(ajd)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Préparer la saison suivante (Jen, 02.11.2026 : « comment l'école va faire en juin pour
   créer le nouvel horaire partagé ? … qu'elle puisse indiquer pour chaque soirée comment elle
   gère ») ── Les soirées viennent du Planning. Pour chacune : toujours les mêmes profs, en
   alternance (2 à 6 équipes, une semaine chacune), ou comme cette année (la répartition la plus
   fréquente). Toutes les semaines de la saison sont remplies, vacances et fériés sautés ; une
   date déjà réglée n'est jamais remplacée sans « Remplacer » ou « Garder ce qui est déjà réglé ». */
const LETTRES = "ABCDEF";
const VERT = "#15803D";
const pluriel = (n, mot, motPluriel = `${mot}s`) => `${n} ${n > 1 ? motPluriel : mot}`;
const sansExtra = (lignes) => lignes.map(({ nom, debut, fin }) => ({ nom, debut, fin }));

const EtatSoiree = ({ so }) => {
  const partiel = so.remplies > 0 && !so.prete;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap"
      style={so.prete ? { background: "#F0FDF4", color: VERT } : { background: "#FFF7ED", color: ORANGE }} data-testid={`horaires-prep-etat-${so.id}`}>
      {so.prete ? <Check size={13} /> : <Clock size={13} />}
      {so.prete ? `Prête · ${pluriel(so.remplies, "semaine remplie", "semaines remplies")}`
        : partiel ? `À préparer · ${so.remplies} semaine${so.remplies > 1 ? "s" : ""} sur ${so.semaines}` : "À préparer"}
    </span>
  );
};

const Choix = ({ actif, titre, texte, onClick, desactive = false, testid }) => (
  <button type="button" role="radio" aria-checked={actif} disabled={desactive} onClick={onClick} data-testid={testid}
    className="text-left rounded-2xl border px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    style={actif ? { borderColor: "var(--primary)", boxShadow: "0 0 0 1px var(--primary)", background: "#fff" } : { borderColor: TRAIT, background: "#fff" }}>
    <div className="flex items-center gap-2 text-[14.5px] font-semibold">
      <span className="inline-flex items-center justify-center rounded-full shrink-0" style={{ width: 16, height: 16, border: `1.5px solid ${actif ? "var(--primary)" : "#CBD5E1"}` }}>
        {actif && <span className="rounded-full" style={{ width: 8, height: 8, background: "var(--primary)" }} />}
      </span>
      {titre}
    </div>
    <div className="text-xs text-[var(--muted)] mt-1 leading-snug">{texte}</div>
  </button>
);

const CartePreparation = ({ so, p, peut, onFait }) => {
  const [ouverte, setOuverte] = useState(!so.prete);
  const [facon, setFacon] = useState("");              // memes | alternance | comme_cette_annee
  const [lignes, setLignes] = useState([]);
  const [equipes, setEquipes] = useState([[], []]);
  const [deja, setDeja] = useState("");                // remplacer | garder
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState("");
  const ref = so.comme_cette_annee;
  const comme = p.cette_annee ? "Comme cette année" : `Comme en ${p.etiquette_avant}`;
  const courts = nomsCourts(so.cours);
  const pret = facon === "memes" ? partsValides(lignes)
    : facon === "alternance" ? equipes.every(partsValides)
      : facon === "comme_cette_annee" ? !!ref : false;
  const rienAFaire = deja === "garder" && so.prete;
  const peutEnvoyer = pret && (!so.deja || deja) && !rienAFaire && !envoi;
  const envoyer = async () => {
    setEnvoi(true); setErreur(""); setFait("");
    try {
      const r = await api(`/horaires-partages/preparer/${encodeURIComponent(so.id)}`, {
        method: "POST",
        body: JSON.stringify({
          saison: p.saison, facon, deja: so.deja ? deja : "",
          equipes: facon === "memes" ? [sansExtra(lignes)] : facon === "alternance" ? equipes.map(sansExtra) : [],
        }),
      });
      onFait(r);
      setFait(r.message || ""); setOuverte(false); setFacon(""); setDeja(""); setLignes([]); setEquipes([[], []]);
    } catch (e) { setErreur(e.message); }
    setEnvoi(false);
  };
  const choisir = (f) => { setFacon(f); setErreur(""); };
  return (
    <div className="rounded-3xl border bg-white overflow-hidden" style={{ borderColor: TRAIT }} data-testid={`horaires-prep-carte-${so.id}`}>
      <div className="px-5 pt-4 pb-3 flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-[15rem]">
          <div className="font-display text-lg tracking-tight leading-tight">{majuscule(so.jour)} · {h(so.debut)} – {h(so.fin)}</div>
          <div className="text-sm text-[var(--muted)] mt-0.5">{so.titre}</div>
        </div>
        <EtatSoiree so={so} />
      </div>
      {/* La soirée telle que le Planning la donne : ses cours, leurs heures, leurs profs. */}
      <div className="mx-5 mb-4 rounded-2xl overflow-hidden" style={{ background: "#F8FAFC" }}>
        {so.cours.map((c, i) => (
          <div key={c.id} className="px-4 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm" style={i ? { borderTop: "1px solid #fff" } : undefined}>
            <span className="tabular-nums text-[var(--muted)] w-[6.5rem] shrink-0">{h(c.debut)} – {h(c.fin)}</span>
            <span className="flex-1 min-w-[8rem]">{courts[c.id]}</span>
            <span className="inline-flex flex-wrap items-center gap-x-2 text-xs text-[var(--muted)]">
              {c.profs.map((n) => <span key={n} className="inline-flex items-center gap-1"><Pastille nom={n} />{prenom(n)}</span>)}
            </span>
          </div>
        ))}
      </div>

      {fait && !ouverte && (
        <p className="mx-5 mb-4 text-sm rounded-xl px-3 py-2" style={{ color: VERT, background: "#F0FDF4" }} data-testid="horaires-prep-message">{fait}</p>
      )}

      {!ouverte ? (
        peut && (
          <div className="px-5 pb-4 flex flex-wrap items-center gap-3 text-xs text-[var(--muted)]">
            <span className="flex-1 min-w-[200px]">Chaque semaine reste modifiable une par une dans le calendrier de la saison.</span>
            <button type="button" className="btn-ghost text-sm" onClick={() => { setOuverte(true); setFait(""); }} data-testid="horaires-prep-changer">Changer</button>
          </div>
        )
      ) : !peut ? null : (
        <div className="px-5 pb-5 space-y-4 border-t pt-4" style={{ borderColor: TRAIT }}>
          <div className="text-[15px] font-semibold">Comment tu gères cette soirée ?</div>
          <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Comment tu gères cette soirée ?">
            <Choix actif={facon === "memes"} titre="Toujours les mêmes profs" testid="horaires-prep-memes"
              texte="Les mêmes profs, aux mêmes heures, chaque semaine." onClick={() => choisir("memes")} />
            <Choix actif={facon === "alternance"} titre="En alternance" testid="horaires-prep-alternance"
              texte="De 2 à 6 équipes qui tournent, une semaine chacune." onClick={() => choisir("alternance")} />
            <Choix actif={facon === "comme_cette_annee"} titre={comme} testid="horaires-prep-comme" desactive={!ref}
              texte={ref ? `Le plus souvent en ${ref.etiquette} : ${quiTexte(ref.profs)}.` : `Rien de réglé en ${p.etiquette_avant} pour cette soirée.`}
              onClick={() => choisir("comme_cette_annee")} />
          </div>

          {facon === "memes" && (
            <div className="space-y-4" data-testid="horaires-prep-panneau-memes">
              <EditeurParts soiree={so} lignes={lignes} setLignes={setLignes} />
            </div>
          )}

          {facon === "alternance" && (
            <div className="space-y-3" data-testid="horaires-prep-panneau-alternance">
              <p className="text-xs text-[var(--muted)]">
                Une semaine chacune, dans l'ordre ({equipes.map((_, i) => LETTRES[i]).join(", ")}, puis on recommence), de la rentrée
                à la fin de la saison. Les vacances et les fériés ne comptent pas : l'équipe suivante reprend après.
              </p>
              {equipes.map((e, i) => (
                <div key={i} className="rounded-2xl p-3 sm:p-4 space-y-3" style={{ background: "#F8FAFC" }} data-testid={`horaires-prep-equipe-${LETTRES[i]}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Semaine {LETTRES[i]}</span>
                    <span className="text-xs text-[var(--muted)]">
                      semaines {[0, 1, 2].map((k) => i + 1 + k * equipes.length).join(", ")}…
                    </span>
                    {equipes.length > 2 && (
                      <button type="button" className="ml-auto text-xs text-[var(--muted)] hover:text-[#B91C1C]"
                        onClick={() => setEquipes(equipes.filter((_, k) => k !== i))} data-testid={`horaires-prep-retirer-${LETTRES[i]}`}>Retirer</button>
                    )}
                  </div>
                  <EditeurParts soiree={so} lignes={e} recu={false}
                    setLignes={(l) => setEquipes((tout) => tout.map((x, k) => (k === i ? l : x)))} />
                </div>
              ))}
              {equipes.length < 6 && (
                <button type="button" className="btn-ghost text-sm" onClick={() => setEquipes([...equipes, []])} data-testid="horaires-prep-ajouter-equipe">
                  <Plus size={14} /> Ajouter une semaine
                </button>
              )}
            </div>
          )}

          {facon === "comme_cette_annee" && ref && (
            <div className="space-y-3" data-testid="horaires-prep-panneau-comme">
              <div className="rounded-2xl border" style={{ borderColor: TRAIT }}>
                {ref.profs.map((x, i) => (
                  <div key={`${x.nom}-${i}`} className="px-4 py-2.5 flex items-center gap-3 text-sm" style={i ? { borderTop: `1px solid ${TRAIT}` } : undefined}>
                    <Pastille nom={x.nom} taille={10} />
                    <span className="w-[4.5rem] shrink-0 font-medium">{prenom(x.nom)}</span>
                    <span className="tabular-nums">{h(x.debut)} – {h(x.fin)}</span>
                    <span className="ml-auto text-xs text-[var(--muted)] tabular-nums">{duree(enMin(x.fin) - enMin(x.debut))}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[var(--muted)]">
                Le plus souvent en {ref.etiquette} : {ref.fois} {so.jour}{ref.fois > 1 ? "s" : ""} sur les {ref.sur} réglés. Toutes les semaines de {p.etiquette} prennent
                cette répartition ; tu pourras changer une semaine ensuite.
              </p>
              {ref.fois * 2 < ref.sur && (
                <p className="text-xs flex items-start gap-1.5" style={{ color: ORANGE }} data-testid="horaires-prep-conseil">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" /> Cette année, la soirée changeait souvent d'équipe : « En alternance » sera peut-être plus juste.
                </p>
              )}
              <ParCours soiree={so} lignes={ref.profs} />
            </div>
          )}

          {/* Ne jamais écraser une date déjà réglée sans le dire (Jen, 02.11.2026). */}
          {facon && so.deja > 0 && (
            <div className="rounded-2xl px-4 py-3 space-y-2.5" style={{ background: "#FFFBEB" }} data-testid="horaires-prep-deja">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" style={{ color: ORANGE }} />
                <span>{pluriel(so.deja, "date")} de {p.etiquette} {so.deja > 1 ? "sont déjà réglées" : "est déjà réglée"} pour cette soirée.</span>
              </div>
              <div className="flex w-full rounded-full p-0.5 text-sm" style={{ background: "#FEF3C7" }}>
                {[["remplacer", "Remplacer"], ["garder", "Garder ce qui est déjà réglé"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setDeja(v)} data-testid={`horaires-prep-deja-${v}`}
                    className="flex-1 px-2 py-1.5 rounded-full leading-tight" style={deja === v ? { background: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(15,27,45,.12)" } : { color: "#92400E" }}>{l}</button>
                ))}
              </div>
              {deja && (
                <p className="text-xs" style={{ color: "#92400E" }}>
                  {deja === "remplacer" ? "Toutes les semaines prennent ce choix, aussi celles déjà réglées."
                    : rienAFaire ? "Toutes les semaines sont déjà réglées : rien ne changerait. Choisis « Remplacer » pour appliquer ce choix."
                      : "Seules les semaines encore vides sont remplies ; les autres ne bougent pas."}
                </p>
              )}
            </div>
          )}

          {erreur && <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }} data-testid="horaires-prep-erreur">{erreur}</p>}

          {facon && (
            <div className="flex flex-wrap items-center gap-2">
              {so.prete && <button type="button" className="btn-ghost text-sm" onClick={() => { setOuverte(false); setFacon(""); setErreur(""); }}>Annuler</button>}
              <button type="button" className="btn-primary text-sm ml-auto" disabled={!peutEnvoyer} onClick={envoyer} data-testid="horaires-prep-remplir">
                <Check size={14} /> {envoi ? "Enregistrement…" : deja === "garder" && so.deja ? "Remplir les semaines encore vides"
                  : `Remplir les ${so.semaines} semaines${facon === "alternance" ? " en alternance" : ""}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Preparer = ({ saison, peut, onRetour }) => {
  const [p, setP] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    api(`/horaires-partages/preparer?saison=${encodeURIComponent(saison)}`).then(setP).catch((e) => setErreur(e.message));
  }, [saison]);
  useEffect(() => { window.scrollTo?.(0, 0); }, []);
  const total = p?.soirees.length || 0;
  const pretes = total - (p?.a_preparer || 0);
  return (
    <div className="space-y-5 max-w-3xl" data-testid="horaires-preparer-saison">
      <button type="button" className="btn-ghost text-sm" onClick={onRetour} data-testid="horaires-prep-retour"><ArrowLeft size={14} /> Calendrier {p?.etiquette || ""}</button>
      <div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">Préparer la saison {p?.etiquette || saison.replace("-", "/")}</h2>
        {p && (
          <p className="text-sm text-[var(--muted)] mt-1">
            Du {dateLongue(p.du).toLowerCase()} au {dateLongue(p.au).toLowerCase()}. Pour chaque soirée, dis comment tu la gères :
            toutes les semaines sont remplies d'un coup, vacances et fériés sautés.
          </p>
        )}
      </div>
      {erreur && <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }}>{erreur}</p>}
      {!p ? (!erreur && <p className="text-sm text-[var(--muted)]">Chargement…</p>) : !total ? (
        <p className="text-sm text-[var(--muted)]">Aucune soirée de comédie musicale au Planning.</p>
      ) : (
        <>
          <div className="flex items-center gap-3" data-testid="horaires-prep-progres">
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "#EEF2F6" }}>
              <div className="h-full rounded-full" style={{ width: `${(pretes / total) * 100}%`, background: VERT }} />
            </div>
            <span className="text-xs text-[var(--muted)] tabular-nums whitespace-nowrap">{pretes} soirée{pretes > 1 ? "s" : ""} prête{pretes > 1 ? "s" : ""} sur {total}</span>
          </div>
          {p.soirees.map((so) => <CartePreparation key={so.id} so={so} p={p} peut={peut} onFait={setP} />)}
        </>
      )}
      <p className="text-xs text-[var(--muted)] max-w-2xl" data-testid="horaires-prep-planning">
        Les soirées viennent du Planning : les cours de comédie musicale et la Compagnie qui suit, avec leurs profs. Si un cours
        ou un prof change l'an prochain, corrige d'abord le Planning : la soirée suivra ici.
      </p>
    </div>
  );
};

/* Le choix de la saison (Jen, 02.11.2026) : celle en cours, et la suivante dès que son
   calendrier existe — « 2027/2028 (à préparer) », puis « (prête) ». */
const ChoixSaison = ({ saisons, valeur, onChoisir }) => (
  <div className="inline-flex max-w-full rounded-full p-0.5 text-sm" style={{ background: "#F1F5F9" }} role="tablist" aria-label="Saison" data-testid="horaires-saisons">
    {saisons.map((s) => (
      <button key={s.id} type="button" role="tab" aria-selected={valeur === s.id} onClick={() => onChoisir(s.id)} data-testid={`horaires-saison-${s.id}`}
        className="px-3.5 py-1 rounded-full whitespace-nowrap tabular-nums" style={valeur === s.id ? { background: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(15,27,45,.12)" } : { color: "var(--muted)" }}>
        {s.etiquette}{s.a_preparer ? <span className="font-normal"> (à préparer)</span> : s.prete ? <span className="font-normal"> (prête)</span> : null}
      </button>
    ))}
  </div>
);

/* En tête de la saison suivante : où en est la préparation, et le bouton pour la faire. */
const BandeauSaison = ({ d, soirees, peut, onPreparer }) => {
  const reste = soirees.filter((s) => !s.prete).length;
  const n = soirees.length;
  const encore = reste === n ? (n > 1 ? `Les ${n} soirées sont` : "La soirée est") : `${pluriel(reste, "soirée")} sur ${n} ${reste > 1 ? "sont" : "est"}`;
  return (
    <div className="rounded-2xl border bg-white px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-3" style={{ borderColor: TRAIT }} data-testid="horaires-bandeau-saison">
      <div className="flex-1 min-w-[220px]">
        <div className="font-semibold flex items-center gap-2">
          {reste ? <Clock size={15} style={{ color: ORANGE }} /> : <Check size={15} style={{ color: VERT }} />}
          {reste ? `Saison ${d.etiquette} à préparer` : `Saison ${d.etiquette} prête`}
        </div>
        <div className="text-sm text-[var(--muted)] mt-0.5">
          {reste ? `${encore} encore à préparer. Les cours reprennent le ${dateLongue(d.du).toLowerCase()}.`
            : `${n > 1 ? `Les ${n} soirées sont remplies` : "La soirée est remplie"}, de la rentrée à la fin de la saison. Chaque semaine reste modifiable ci-dessous.`}
        </div>
      </div>
      {peut && (
        <button type="button" className={reste ? "btn-primary text-sm" : "btn-ghost text-sm"} onClick={onPreparer} data-testid="horaires-preparer">
          {reste ? `Préparer la saison ${d.etiquette}` : "Revoir la préparation"}
        </button>
      )}
    </div>
  );
};

export default function HorairesPartages({ saisonInitiale = "" }) {
  const [d, setD] = useState(null);
  // La saison montrée : "" = celle en cours ; « 2027-2028 » = la suivante, à préparer (le
  // rappel « À traiter » ouvre l'écran directement sur elle).
  const [saison, setSaison] = useState(saisonInitiale);
  const [preparer, setPreparer] = useState(false);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [absent, setAbsent] = useState(false);
  const [filtre, setFiltre] = useState("");            // "" = tous les jours, sinon « lundi », « jeudi »…
  const [mois, setMois] = useState("");
  const [edite, setEdite] = useState(null);            // { soiree: id, date }
  const [envoi, setEnvoi] = useState(false);
  const [impression, setImpression] = useState(false);
  const charger = useCallback(() => {
    api(`/horaires-partages${saison ? `?saison=${encodeURIComponent(saison)}` : ""}`).then(setD).catch((e) => {
      // Fermé en production tant que l'école n'a pas validé le chantier : rien à montrer.
      if (/bientôt|404/.test(String(e.message))) setAbsent(true); else setErreur(e.message);
    });
  }, [saison]);
  useEffect(charger, [charger]);
  const agir = async (chemin, methode, corps, ensuite) => {
    setErreur(""); setMessage("");
    try {
      const r = await api(chemin, { method: methode, ...(corps ? { body: JSON.stringify(corps) } : {}) });
      setD(r);
      if (ensuite) ensuite(r);
      return r;
    } catch (e) { setErreur(e.message); return null; }
  };
  const fermer = useCallback(() => { setEdite(null); setErreur(""); }, []);

  const soirees = useMemo(() => d?.soirees || [], [d]);
  const coursParId = useMemo(() => Object.fromEntries((d?.cours || []).map((c) => [c.id, c])), [d]);
  // Toutes les dates de la saison, avec les soirées de chaque date.
  const parDate = useMemo(() => {
    const m = {};
    soirees.forEach((so) => so.seances.forEach((s) => { (m[s.date] ||= []).push({ soiree: so, seance: s }); }));
    return m;
  }, [soirees]);
  const jours = useMemo(() => [...new Set(soirees.map((s) => s.jour))], [soirees]);
  const tousMois = useMemo(() => [...new Set(Object.keys(parDate).map((x) => x.slice(0, 7)))].sort(), [parDate]);
  const ajd = aujourdhui();
  // À l'ouverture : le mois de la prochaine date (Jen : « on doit venir au prochain horaire »).
  useEffect(() => {
    if (mois || !tousMois.length) return;
    const prochaine = Object.keys(parDate).sort().find((x) => x >= ajd && parDate[x].some((e) => e.seance.source !== "calendrier"));
    setMois(prochaine ? prochaine.slice(0, 7) : tousMois[tousMois.length - 1]);
  }, [tousMois, mois, ajd, parDate]);

  if (absent) return null;
  const peut = d?.peut_modifier;
  const dates = Object.keys(parDate).filter((x) => x.startsWith(mois)).sort();
  const visibles = dates.map((x) => ({ date: x, soirees: parDate[x].filter((e) => !filtre || e.soiree.jour === filtre) }))
    .filter((x) => x.soirees.length);
  // Les vacances qui se suivent tiennent sur une seule ligne.
  const lignesListe = [];
  visibles.forEach((x) => {
    const vacances = x.soirees.every((e) => e.seance.source === "calendrier");
    const note = vacances ? x.soirees[0].seance.note : "";
    const der = lignesListe[lignesListe.length - 1];
    if (vacances && der?.vacances && der.note === note) der.fin = x.date;
    else lignesListe.push({ ...x, vacances, note, fin: x.date });
  });
  // « prochain » : la vraie prochaine date (du filtre), pas la première de chaque mois à venir.
  const prochaine = Object.keys(parDate).sort().find((x) => x >= ajd && parDate[x].some((e) => (!filtre || e.soiree.jour === filtre)
    && e.seance.source !== "calendrier" && !e.seance.ferme));
  // Les heures prévues du mois, par prof (toutes les soirées) : ce qui arrive dans le décompte.
  const resume = {};
  dates.forEach((x) => parDate[x].forEach(({ seance }) => {
    if (seance.ferme) return;
    seance.profs.forEach((p) => {
      const r = (resume[prenom(p.nom)] ||= { minutes: 0, dates: new Set() });
      r.minutes += enMin(p.fin) - enMin(p.debut);
      r.dates.add(x);
    });
  }));
  const aRegler = dates.filter((x) => x >= ajd && parDate[x].some(({ seance }) => seance.source === "a_verifier")).length;

  const chemin = (so, suite) => `/horaires-partages/soiree/${encodeURIComponent(so.id)}${suite}`;
  // Les dates suivantes de la soirée : jusqu'aux prochaines vacances, ou toute la saison
  // (vacances et fériés sautés) ; un jour déjà fermé par l'école (Spectacle de Noël…) reste fermé.
  const suivantes = (so, date) => {
    const apres = so.seances.filter((x) => x.date > date);
    const fin = apres.findIndex((x) => x.source === "calendrier");
    return (fin < 0 ? apres : apres.slice(0, fin)).filter((x) => !x.ferme);
  };
  const saisonApres = (so, date) => so.seances.filter((x) => x.date > date && x.source !== "calendrier" && !x.ferme);
  const enregistrer = async (so, s, corps, portee) => {
    setEnvoi(true);
    const autres = portee === "saison" ? saisonApres(so, s.date) : portee === "vacances" ? suivantes(so, s.date) : [];
    const r = autres.length
      ? await agir(chemin(so, "/seances"), "PUT", { ...corps, dates: [s.date, ...autres.map((x) => x.date)] })
      : await agir(chemin(so, `/seance/${s.date}`), "PUT", corps);
    setEnvoi(false);
    if (r) { setEdite(null); setMessage(r.message || `${dateLongue(s.date)} enregistré.`); }
  };
  // Changer de mois efface le message de la dernière action (il restait d'un mois à l'autre).
  const allerAu = (m) => { setMois(m); setMessage(""); };
  // Une proposition sans répartition connue : ouvrir directement la date à régler.
  const regler = (p) => {
    const so = soirees.find((x) => x.cours.some((c) => c.id === p.cours_id));
    if (!so) return;
    setMois(p.date.slice(0, 7)); setErreur(""); setEdite({ soiree: so.id, date: p.date });
  };
  const ouverte = edite && soirees.find((x) => x.id === edite.soiree);
  const seanceOuverte = ouverte && ouverte.seances.find((x) => x.date === edite.date);
  const indexMois = tousMois.indexOf(mois);

  // Changer de saison : tout l'écran (mois, dates, impression, « Toute la saison ») suit.
  const choisirSaison = (s) => {
    if (s === d?.saison) return;
    setD(null); setMois(""); setMessage(""); setErreur(""); setEdite(null); setImpression(false); setPreparer(false);
    setSaison(s);
  };
  const suivante = d && d.courante === false;

  if (impression && d) {
    return <Impression soirees={soirees} parDate={parDate} mois={mois} saison={d.etiquette} aujourdhui={ajd} onRetour={() => setImpression(false)} />;
  }
  if (preparer && d) {
    return <Preparer saison={d.saison} peut={peut} onRetour={() => { setPreparer(false); setMessage(""); setD(null); charger(); }} />;
  }

  return (
    <div className="space-y-5 max-w-4xl" data-testid="horaires-editeur">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        {(d?.saisons || []).length > 1 && <ChoixSaison saisons={d.saisons} valeur={d.saison} onChoisir={choisirSaison} />}
        <p className="text-sm text-[var(--muted)] max-w-3xl flex-1 min-w-[260px]">
          Les cours donnés à plusieurs profs, date par date : qui enseigne et de quelle heure à quelle heure. Les heures du mois
          de chaque prof en découlent.
        </p>
      </div>
      {message && <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#15803D", background: "#F0FDF4" }} data-testid="horaires-message">{message}</p>}
      {erreur && !edite && <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }}>{erreur}</p>}
      {suivante && soirees.length > 0 && <BandeauSaison d={d} soirees={soirees} peut={peut} onPreparer={() => { setMessage(""); setPreparer(true); }} />}
      {!suivante && <Propositions liste={d?.propositions} peutModifier={peut} agir={agir} onMessage={setMessage} onRegler={regler} />}

      {!d ? <p className="text-sm text-[var(--muted)]">Chargement…</p>
        : !soirees.length ? <p className="text-sm text-[var(--muted)]">Aucun cours à plusieurs profs.</p> : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center rounded-full border bg-white p-0.5" style={{ borderColor: TRAIT }}>
                <button type="button" className="p-1.5 rounded-full hover:bg-[#F1F5F9] disabled:opacity-30" disabled={indexMois <= 0}
                  onClick={() => allerAu(tousMois[indexMois - 1])} aria-label="Mois précédent" data-testid="horaires-mois-precedent"><ChevronLeft size={16} /></button>
                <span className="px-2 text-sm font-semibold w-36 text-center" data-testid="horaires-mois">{mois ? nomMois(mois) : ""}</span>
                <button type="button" className="p-1.5 rounded-full hover:bg-[#F1F5F9] disabled:opacity-30" disabled={indexMois >= tousMois.length - 1}
                  onClick={() => allerAu(tousMois[indexMois + 1])} aria-label="Mois suivant" data-testid="horaires-mois-suivant"><ChevronRight size={16} /></button>
              </div>
              {mois !== ajd.slice(0, 7) && tousMois.includes(ajd.slice(0, 7)) && (
                <button type="button" className="text-sm font-medium" style={{ color: "var(--primary)" }} onClick={() => allerAu(ajd.slice(0, 7))} data-testid="horaires-aujourdhui">Aujourd'hui</button>
              )}
              <button type="button" className="btn-ghost text-sm" onClick={() => setImpression(true)} data-testid="horaires-imprimer">
                <Printer size={14} /> Imprimer
              </button>
              {jours.length > 1 && (
                <div className="inline-flex rounded-full p-0.5 text-sm ml-auto" style={{ background: "#F1F5F9" }}>
                  {[["", "Tous"], ...jours.map((j) => [j, `${majuscule(j)}s`])].map(([v, l]) => (
                    <button key={l} type="button" onClick={() => setFiltre(v)} data-testid={`horaires-filtre-${v || "tous"}`}
                      className="px-3.5 py-1 rounded-full" style={filtre === v ? { background: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(15,27,45,.12)" } : { color: "var(--muted)" }}>{l}</button>
                  ))}
                </div>
              )}
            </div>

            {Object.keys(resume).length > 0 && (
              <div data-testid="horaires-resume">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Prévu en {MOIS[Number(mois.slice(5, 7)) - 1]}</div>
                  {aRegler > 0 && <div className="text-xs" style={{ color: ORANGE }}>{aRegler} date{aRegler > 1 ? "s" : ""} à régler</div>}
                </div>
                {/* Les traits entre les cases, sans case grise au bout d'une ligne incomplète
                    (3 profs sur 4 colonnes) : bord à droite et en bas, le dernier rogné. */}
                <div className="rounded-2xl overflow-hidden border bg-white" style={{ borderColor: TRAIT }}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 -mr-px -mb-px">
                    {Object.entries(resume).sort((a, b) => b[1].minutes - a[1].minutes).map(([n, r]) => (
                      <div key={n} className="px-4 py-3 border-r border-b" style={{ borderColor: TRAIT }} data-testid={`horaires-resume-${n}`}>
                        <div className="flex items-center gap-1.5 text-xs text-[var(--muted)]"><Pastille nom={n} />{n}</div>
                        <div className="text-xl font-semibold tabular-nums mt-0.5">{duree(r.minutes)}</div>
                        <div className="text-[11px] text-[var(--muted)]">{r.dates.size} date{r.dates.size > 1 ? "s" : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: TRAIT }} data-testid="horaires-liste">
              {!lignesListe.length && <div className="px-4 py-6 text-sm text-[var(--muted)]">Pas de cours partagé ce mois-ci.</div>}
              {lignesListe.map((x, i) => {
                const d0 = quand(x.date);
                const passee = x.fin < ajd;
                const bord = i ? { borderTop: `1px solid ${TRAIT}` } : {};
                if (x.vacances) {
                  const d1 = quand(x.fin);
                  return (
                    <div key={x.date} className="px-4 py-2.5 flex items-center gap-4 text-sm text-[var(--muted)]" style={{ ...bord, background: "#FAFBFC" }} data-testid={`horaires-date-${x.date}`}>
                      <span className="w-12 text-center text-xs tabular-nums">{d0.getDate()}{x.fin !== x.date ? `–${d1.getDate()}` : ""}</span>
                      <span>{x.note || "Vacances"}</span>
                    </div>
                  );
                }
                return (
                  <div key={x.date} className="flex gap-4 px-4 py-3.5" style={{ ...bord, opacity: passee ? 0.55 : 1 }} data-testid={`horaires-date-${x.date}`}>
                    <div className="w-12 text-center shrink-0 pt-0.5">
                      <div className="text-[11px] font-semibold tracking-wide" style={{ color: x.date === prochaine ? "var(--primary)" : "var(--muted)" }}>{JOURS_COURT[d0.getDay()]}</div>
                      <div className="text-2xl font-semibold leading-none mt-1 tabular-nums" style={x.date === prochaine ? { color: "var(--primary)" } : undefined}>{d0.getDate()}</div>
                      {x.date === prochaine && <div className="text-[10px] font-semibold mt-1" style={{ color: "var(--primary)" }}>prochain</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      {x.soirees.map(({ soiree, seance }, k) => {
                        const manquants = soiree.cours.filter((c) => coursParId[c.id]?.seances.find((y) => y.date === x.date)?.source === "a_verifier").map((c) => c.nom);
                        const cliquable = peut && seance.source !== "calendrier";
                        return (
                          <button key={soiree.id} type="button" disabled={!cliquable}
                            onClick={() => { setErreur(""); setEdite({ soiree: soiree.id, date: x.date }); }}
                            className={`w-full text-left flex items-center gap-3 ${k ? "pt-3 mt-3" : ""} ${cliquable ? "group" : "cursor-default"}`}
                            style={k ? { borderTop: `1px solid ${TRAIT}` } : undefined}
                            data-testid={`horaires-case-${soiree.id}-${x.date}`} title={cliquable ? "Modifier" : undefined}>
                            <div className="flex-1 min-w-0">
                              {seance.source === "calendrier" ? (
                                <div className="text-sm text-[var(--muted)]">{soiree.titre} · {seance.note || "Vacances"}</div>
                              ) : <BlocSoiree soiree={soiree} seance={seance} manquants={manquants} />}
                            </div>
                            {cliquable && <ChevronRight size={16} className="shrink-0 text-[#CBD5E1] group-hover:text-[var(--text)]" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[var(--muted)]">
              Les vacances et les fériés viennent du calendrier de la saison (Réglages). Les heures prévues comptent toutes les dates du mois ;
              le décompte de chaque prof les reprend cours par cours (un cours sans élève ne compte pas).
            </p>
          </>
        )}

      {ouverte && seanceOuverte && (
        <Editeur key={`${ouverte.id}-${seanceOuverte.date}`} soiree={ouverte} seance={seanceOuverte} envoi={envoi} erreur={erreur}
          suivantes={suivantes(ouverte, seanceOuverte.date).length}
          saison={saisonApres(ouverte, seanceOuverte.date).length}
          autres={(parDate[seanceOuverte.date] || []).filter((e) => e.soiree.id !== ouverte.id && !e.seance.ferme)}
          onFermer={fermer}
          onEnregistrer={(corps, portee) => enregistrer(ouverte, seanceOuverte, corps, portee)}
          onEffacer={seanceOuverte.source === "date" ? () => agir(chemin(ouverte, `/seance/${seanceOuverte.date}`), "DELETE", null, () => {
            setEdite(null); setMessage(`${dateLongue(seanceOuverte.date)} : plus personne d'assigné.`);
          }) : null} />
      )}
    </div>
  );
}
